'use client'

import { Dialog } from '@vinted/web-ui'
import { useEffect, useState } from 'react'

import { useFetch } from '@marketplace-web/shared/api-client'
import { reloadPage } from '@marketplace-web/shared/browser'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { ContentLoader } from '@marketplace-web/shared/common-components/content-loader'
import {
  transformLabelOptionsResponse,
  getLabelOptions,
  updateDeliveryOptionChangeRequest,
} from '@marketplace-web/domain/conversation-shipping-data'

import { ResponseCode } from 'data/api/response-codes'

import GenericErrorModal from '../../GenericErrorModal'
import { DeliveryOptionChangeRequestStatus } from '../../../constants'

type Props = {
  isOpen: boolean
  shipmentId?: number
  onClose: () => void
}

const CancelChangeDeliveryOptionRequestModal = ({ isOpen, shipmentId, onClose }: Props) => {
  const translate = useTranslate('conversation.cancel_carrier_change_request_modal')

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)

  const {
    fetch: fetchLabelOptions,
    transformedData: labelOptions,
    isLoading: isLabelOptionsLoading,
  } = useFetch(getLabelOptions, transformLabelOptionsResponse)

  useEffect(() => {
    if (!isOpen || !shipmentId) return

    fetchLabelOptions(shipmentId)
  }, [fetchLabelOptions, isOpen, shipmentId])

  const handleSubmitClick = async () => {
    if (!shipmentId) return

    setIsLoading(true)

    const response = await updateDeliveryOptionChangeRequest({
      shipmentId,
      status: DeliveryOptionChangeRequestStatus.Cancelled,
    })

    if (response.code !== ResponseCode.Ok) {
      setIsLoading(false)
      setError(true)
    }

    if (response.code === ResponseCode.Ok) {
      setIsLoading(false)
      reloadPage()
    }
  }

  const handleModalClose = () => {
    setError(false)
    onClose()
  }

  if (isLoading || isLabelOptionsLoading) {
    return (
      <Dialog show>
        <ContentLoader />
      </Dialog>
    )
  }

  if (error) {
    return <GenericErrorModal isShown onClose={handleModalClose} />
  }

  return (
    <Dialog
      title={translate('title')}
      body={translate('modal_body', {
        carrier_name: labelOptions?.carrier.name,
      })}
      testId="cancel-change-delivery-option-request-modal"
      actions={[
        {
          text: translate('actions.confirm'),
          style: Dialog.ActionStyling.Filled,
          callback: handleSubmitClick,
          testId: 'cancel-change-delivery-option-request-modal-confirm-button',
        },
        {
          text: translate('actions.cancel'),
          style: Dialog.ActionStyling.Outlined,
          callback: handleModalClose,
          testId: 'cancel-change-delivery-option-request-modal-close-button',
        },
      ]}
      show={isOpen}
    />
  )
}

export default CancelChangeDeliveryOptionRequestModal
